// Event co-hosting (Phase B) — client-side mirror of the server-side
// cohost gate (migration 20260716000040). A ticket type may REQUIRE a
// booking of another service ("Barrel-Room Jazz" ticket needs a Stoep
// table). The server pairs each pending ticket booking that carries a
// cohost requirement 1:1 with a pending sibling booking of the required
// service on the same cart, zeroes the sibling's amount for the
// bundled/free_reserve charge modes BEFORE summing, and (when the tenant
// set fallback='block') rejects checkout with reason 'cohost_required'
// for an unpaired REQUIRED ticket.
//
// This module reproduces that pairing over the local cart so the widget
// can (a) auto-guide the guest to the required service after adding a
// required ticket, (b) warn + gate the checkout CTA, and (c) display a
// paired sibling's price as "Included" / "Free — pay on arrival". It is
// a SOFT mirror only — the server remains authoritative; the widget also
// maps the server's 'cohost_required' rejection to a human message.

import type { CartItem } from "./cart";
import type { WidgetTicketCohost } from "../types";

export interface CohostUnmet {
  /** Cart-item id of the requiring ticket line (or a caller-supplied
   *  placeholder id when pairing not-yet-added lines). */
  itemId: string;
  /** Event name, for "Your {event} ticket needs …" copy. */
  eventName: string;
  cohost: WidgetTicketCohost;
}

export interface CohostPairing {
  /** Paired sibling cart-item id → the charge mode of the requirement it
   *  satisfies. bundled/free_reserve siblings are the ones whose amount
   *  the server zeroes (display them as Included / Free). */
  siblingModes: Map<string, WidgetTicketCohost["charge_mode"]>;
  /** REQUIRED tickets with no qualifying sibling left to pair. 'prompt'
   *  tickets never land here (decision: prompt keeps the soft banner only). */
  unmet: CohostUnmet[];
}

/** The cohost requirement stamped on an event-ticket cart line at add time
 *  (EventDetailPanel copies ticket_type.cohost into metadata.cohost). */
export function lineCohost(item: CartItem): WidgetTicketCohost | null {
  const md = item.metadata ?? {};
  if (md.source !== "event") return null;
  const c = md.cohost as WidgetTicketCohost | null | undefined;
  if (!c || typeof c !== "object") return null;
  if (
    typeof c.required_service_id !== "string" ||
    typeof c.required_service_slug !== "string"
  ) {
    return null;
  }
  return c;
}

/**
 * A cart line that can satisfy a cohost requirement: a pending Vertu
 * booking hold (booking_id) of the required service that is not itself an
 * event ticket. Any slot/unit qualifies — the server pairs on service
 * only. ChiDesk lines carry no native booking_id, so they're excluded
 * automatically (they aren't pending bookings on the cart server-side
 * either).
 */
export function isQualifyingSibling(
  item: CartItem,
  cohost: WidgetTicketCohost,
): boolean {
  const md = item.metadata ?? {};
  if (md.source === "event") return false;
  if (typeof md.booking_id !== "string") return false;
  return item.serviceId === cohost.required_service_id;
}

/**
 * Pairs requiring tickets with qualifying siblings 1:1, earliest first
 * (cart order ≈ hold-creation order, mirroring the server's ordering).
 * Each sibling satisfies at most ONE requiring ticket.
 */
export function computeCohostPairing(items: CartItem[]): CohostPairing {
  const siblingModes = new Map<string, WidgetTicketCohost["charge_mode"]>();
  const unmet: CohostUnmet[] = [];
  const used = new Set<string>();
  for (const item of items) {
    const cohost = lineCohost(item);
    if (!cohost) continue;
    const sibling = items.find(
      (s) => !used.has(s.id) && isQualifyingSibling(s, cohost),
    );
    if (sibling) {
      used.add(sibling.id);
      siblingModes.set(sibling.id, cohost.charge_mode);
    } else if (cohost.enforcement === "required") {
      unmet.push({ itemId: item.id, eventName: item.serviceName, cohost });
    }
  }
  return { siblingModes, unmet };
}

/** Notice copy for an unmet requirement — tenant prompt_text wins. */
export function cohostNoticeText(
  cohost: WidgetTicketCohost,
  eventName: string,
): string {
  return (
    cohost.prompt_text?.trim() ||
    `Your ${eventName} ticket needs a ${cohost.required_service_name} booking — add it to complete checkout.`
  );
}
